/**
 * CarouselsDialog.jsx — save the current carousel, and reopen a saved one.
 *
 * A row holds the layout only. Reopening one loads the HDPhotoHub shoots it names
 * first, then puts the slides back; local files cannot follow a carousel to
 * another machine, so those cells come back empty until you re-add the folder.
 */

import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import { useMobile } from '../../hooks/useMobile';
import { useJobs } from '../../hooks/useJobs';
import { useProfiles } from '../../hooks/useProfiles';
import { useHdphSites } from '../../hooks/useRotoTube';
import { useCarousels, useDeleteCarousel, useSaveCarousel } from '../../hooks/useCarousels';
import {
  applyDoc, closeDoc, docPayload, docSaved, loadShoot, toast, useCarousel,
} from '../../hooks/useCarouselStore';

function ago(iso) {
  const t = iso ? new Date(iso).getTime() : 0;
  if (!t) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  if (mins < 60 * 24) return Math.round(mins / 60) + 'h ago';
  const days = Math.round(mins / (60 * 24));
  return days < 30 ? days + 'd ago' : new Date(t).toLocaleDateString();
}

export default function CarouselsDialog({ open, onClose }) {
  const { isMobile } = useMobile();
  const doc = useCarousel((s) => s.doc);
  const slideN = useCarousel((s) => s.slides.length);
  const library = useCarousel((s) => s.library);

  const { data: rows, isLoading } = useCarousels(open);
  const { data: jobs } = useJobs();
  const { data: profiles } = useProfiles();
  const { data: sites, refetch: refetchSites } = useHdphSites(open);
  const saveMut = useSaveCarousel();
  const delMut = useDeleteCarousel();

  const [title, setTitle] = useState('');
  const [jobText, setJobText] = useState('');
  const [busy, setBusy] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const jobList = useMemo(() => (jobs || []).filter((j) => j.title), [jobs]);
  const jobTitleById = useMemo(() => new Map(jobList.map((j) => [j.id, j.title])), [jobList]);
  const jobIdByTitle = useMemo(() => {
    const m = new Map();
    jobList.forEach((j) => { if (!m.has(j.title)) m.set(j.title, j.id); });
    return m;
  }, [jobList]);
  const nameById = useMemo(
    () => new Map((profiles || []).map((p) => [p.id, p.name || p.fullName || p.email || ''])),
    [profiles],
  );
  const loadedSids = useMemo(() => {
    const s = new Set();
    library.forEach((im) => { if (im.source === 'hdph') s.add(im.sid); });
    return s;
  }, [library]);

  // Opening the dialog offers to update what is already open, or name a new one.
  useEffect(() => {
    if (!open) return;
    setTitle(doc ? doc.title : '');
    setJobText(doc && doc.jobId ? (jobTitleById.get(doc.jobId) || '') : '');
    setConfirmDel(null);
  }, [open, doc, jobTitleById]);

  const jobId = jobText ? (jobIdByTitle.get(jobText) || null) : null;

  const save = async (asNew) => {
    if (!slideN) { toast('Build some slides first.'); return; }
    setBusy('save');
    try {
      const body = docPayload(title, jobId);
      const row = await saveMut.mutateAsync(asNew || !doc ? body : { id: doc.id, ...body });
      docSaved(row);
      onClose();
    } catch (err) {
      toast('Could not save: ' + (err?.message || 'unknown error'), 4200);
    } finally {
      setBusy(null);
    }
  };

  const openRow = async (row) => {
    setBusy(row.id);
    try {
      const shoots = (row.source && row.source.shoots) || [];
      const missing = shoots.filter((sh) => !loadedSids.has(sh.sid));
      if (missing.length) {
        let list = sites;
        if (!list) list = (await refetchSites()).data;
        for (const sh of missing) {
          const site = (list || []).find((s) => s.sid === sh.sid);
          if (site) await loadShoot(site);
        }
      }
      if (row.source && row.source.hasLocal) {
        toast('This carousel also used files from your computer — add them to fill those cells.', 5200);
      }
      if (applyDoc(row)) onClose();
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row) => {
    setBusy(row.id);
    try {
      await delMut.mutateAsync(row.id);
      if (doc && doc.id === row.id) closeDoc();
      toast('Deleted “' + row.title + '”');
    } catch (err) {
      toast('Could not delete: ' + (err?.message || 'unknown error'), 4200);
    } finally {
      setBusy(null);
      setConfirmDel(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      fullScreen={isMobile}
      className="cc-modal"
      aria-labelledby="cc-docs-title"
      slotProps={{
        paper: { className: 'cc-root export-sheet cc-docs' + (isMobile ? ' is-phone' : '') },
        backdrop: { style: { background: 'rgba(12,12,14,.66)' } },
      }}
    >
      <div className="sheet-head">
        <h3 id="cc-docs-title">Carousels</h3>
        <span className="hint">{doc ? 'Editing “' + doc.title + '”' : 'Not saved yet'}</span>
        <button type="button" className="btn tiny ghost" onClick={onClose} disabled={!!busy}>Close</button>
      </div>

      <div className="export-opts cc-savebar">
        <label className="ctl">
          <span className="lbl">Name</span>
          <input
            type="text"
            className="cc-title-input"
            value={title}
            placeholder="Untitled carousel"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="ctl">
          <span className="lbl">Job</span>
          <input
            type="text"
            className="cc-job-input"
            list="cc-job-options"
            value={jobText}
            placeholder="optional"
            onChange={(e) => setJobText(e.target.value)}
          />
          <datalist id="cc-job-options">
            {jobList.slice(0, 400).map((j) => <option key={j.id} value={j.title} />)}
          </datalist>
        </label>
        <button type="button" className="btn primary" disabled={!slideN || !!busy} onClick={() => save(false)}>
          {busy === 'save' ? 'Saving…' : doc ? 'Save' : 'Save carousel'}
        </button>
        {doc && (
          <button type="button" className="btn tiny ghost" disabled={!!busy} onClick={() => save(true)}>
            Save as new
          </button>
        )}
        {jobText && !jobId && <span className="hint">No job matches that name — it will be saved without a job.</span>}
      </div>

      <ol className="export-list">
        {isLoading && <li className="sheet-none">Loading…</li>}
        {!isLoading && !(rows || []).length && <li className="sheet-none">No saved carousels yet.</li>}
        {(rows || []).map((row) => (
          <li className={'xrow cc-docrow' + (doc && doc.id === row.id ? ' is-open' : '')} key={row.id}>
            <div className="xinfo">
              <span className="xname">{row.title}</span>
              <span className="xmeta lbl">
                {row.slide_count || 0} slides
                {row.job_id && jobTitleById.get(row.job_id) ? ' · ' + jobTitleById.get(row.job_id) : ''}
                {row.created_by && nameById.get(row.created_by) ? ' · ' + nameById.get(row.created_by) : ''}
                {row.updated_at ? ' · ' + ago(row.updated_at) : ''}
              </span>
            </div>
            {confirmDel === row.id ? (
              <>
                <span className="hint">Delete it?</span>
                <button type="button" className="btn tiny" disabled={!!busy} onClick={() => remove(row)}>Delete</button>
                <button type="button" className="btn tiny ghost" onClick={() => setConfirmDel(null)}>Keep</button>
              </>
            ) : (
              <>
                <button type="button" className="btn tiny" disabled={!!busy} onClick={() => openRow(row)}>
                  {busy === row.id ? 'Opening…' : 'Open'}
                </button>
                <button
                  type="button"
                  className="btn tiny ghost"
                  title="Delete this carousel"
                  aria-label={'Delete ' + row.title}
                  disabled={!!busy}
                  onClick={() => setConfirmDel(row.id)}
                >
                  ✕
                </button>
              </>
            )}
          </li>
        ))}
      </ol>
      <div className="export-foot">
        <span className="hint">Saved carousels hold the layout only, and everyone on the team can open them.</span>
      </div>
    </Dialog>
  );
}
